/**
 * Component: UserTierOverview
 * Doughnut chart representing user tier distribution.
 */

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  ChartContainer,
  type ChartConfig,
} from "@/components/ui/chart/chart";
import {
  tierBadgeStyles,
  tierTooltipDotColors,
} from "../../../../mock";
import { CustomerActiveIcon, ExternalIcon } from "@/assets/svg";
import { formatToK } from "@/utils/Format";
import type { UserTiersData } from "../../../../domain/model/DashboardModel";

type TierChartItem = {
  name: string;
  value: number;
  percentage: number;
};

export interface UserTierOverviewProps {
  data: UserTiersData | null;
}

const tierCodeToDisplayName: Record<string, string> = {
  DIAMOND: "Diamond",
  PLATINUM: "Platinum",
  GOLD: "Gold",
  SILVER: "Silver",
  BRONZE: "Bronze",
};

const gradients: Record<string, string> = {
  Diamond:
    "linear-gradient(75.07deg, #120041 17.5%, #1E0B9C 39.6%, #364BA1 56.18%, #6831C2 77.43%, #4E0F6E 92.73%)",
  Platinum: "linear-gradient(88.6deg, #0F0F0F 0%, #343537 50%, #70757B 100%)",
  Gold: "linear-gradient(88.6deg, #AF7500 0%, #C38723 50%, #D7B94E 99.99%)",
  Silver: "linear-gradient(88.6deg, #5E6366 0%, #4F5355 50%, #B8BCBF 100%)",
  Bronze: "linear-gradient(88.6deg, #8B4513 0%, #CD853F 50%, #DEB887 100%)",
};

function buildChartData(data: UserTiersData | null): TierChartItem[] {
  if (!data) return [];
  return data.distribution.map((item) => {
    const name = tierCodeToDisplayName[item.tierCode] ?? item.tierCode;
    return {
      name,
      value: item.count,
      percentage: item.percentage,
    };
  });
}

function buildChartConfig(chartData: TierChartItem[]): ChartConfig {
  return chartData.reduce<ChartConfig>((config, tier) => {
    return {
      ...config,
      [tier.name]: {
        label: tier.name,
        color: gradients[tier.name as keyof typeof gradients],
      },
    };
  }, {} as ChartConfig);
}

// Custom tooltip component
const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0];
  const tierName = data.name;
  const dotColor = tierTooltipDotColors[tierName as keyof typeof tierTooltipDotColors] || "#64748b";
  
  return (
    <div className="bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-semibold relative after:content-[''] after:absolute after:bottom-[-6px] after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0 after:border-l-[6px] after:border-l-transparent after:border-r-[6px] after:border-r-transparent after:border-t-[6px] after:border-t-slate-800">
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
      {formatToK(data.value)}
    </div>
  );
};

export const UserTierOverview = ({ data }: UserTierOverviewProps): React.JSX.Element => {
  const chartData = buildChartData(data);
  const chartConfig = buildChartConfig(chartData);

  if (!data || chartData.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Tier Overview</h3>
        </div>
        <div className="flex items-center justify-center h-[200px] text-gray-500">
          No tier data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <h3 className="m-0 text-slate-900 font-bold text-base leading-6">User Tier Overview</h3>
        <button
          className="bg-transparent border-none cursor-pointer p-0 m-0 flex items-center justify-center hover:opacity-80 focus:outline-none active:opacity-60"
          aria-label="Open external link"
        >
          <ExternalIcon />
        </button>
      </div>
      <div className="flex flex-col items-center gap-6">
        <ChartContainer
          config={chartConfig}
          aria-label="User tier distribution doughnut chart"
          className="relative w-[200px] h-[200px]"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                <linearGradient
                  id="gradientDiamond"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="17.5%" stopColor="#120041" />
                  <stop offset="39.6%" stopColor="#1E0B9C" />
                  <stop offset="56.18%" stopColor="#364BA1" />
                  <stop offset="77.43%" stopColor="#6831C2" />
                  <stop offset="92.73%" stopColor="#4E0F6E" />
                </linearGradient>
                <linearGradient
                  id="gradientPlatinum"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#0F0F0F" />
                  <stop offset="50%" stopColor="#343537" />
                  <stop offset="100%" stopColor="#70757B" />
                </linearGradient>
                <linearGradient
                  id="gradientGold"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#AF7500" />
                  <stop offset="50%" stopColor="#C38723" />
                  <stop offset="99.99%" stopColor="#D7B94E" />
                </linearGradient>
                <linearGradient
                  id="gradientSilver"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#5E6366" />
                  <stop offset="50%" stopColor="#4F5355" />
                  <stop offset="100%" stopColor="#B8BCBF" />
                </linearGradient>
                <linearGradient
                  id="gradientBronze"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#8B4513" />
                  <stop offset="50%" stopColor="#CD853F" />
                  <stop offset="100%" stopColor="#DEB887" />
                </linearGradient>
              </defs>
	              <Pie
	                data={chartData}
	                dataKey="value"
	                nameKey="name"
                innerRadius="60%"
                outerRadius="85%"
                paddingAngle={5}
                strokeWidth={0}
	              >
	                {chartData.map((tier) => (
	                  <Cell key={tier.name} fill={`url(#gradient${tier.name})`} />
	                ))}
	              </Pie>
              <Tooltip content={<CustomTooltip />} offset={-55} />
            </PieChart>
          </ResponsiveContainer>
	        </ChartContainer>
	        <ul className="list-none m-0 p-0 w-full flex flex-col gap-3">
	          {chartData.map((tier) => (
	            <li key={tier.name} className="flex justify-between items-center gap-3 text-sm">
              <div className="flex items-center gap-3">
                <span
                  className="py-1 px-2 rounded text-center w-[45px] font-medium text-sm leading-5"
                  style={{
	                    background: tierBadgeStyles[tier.name].background,
	                    color: tierBadgeStyles[tier.name].color,
                  }}
                >
	                  {Math.floor(tier.percentage)}%
                </span>
	                <span className="text-slate-900 font-normal text-sm leading-5">{tier.name}</span>
              </div>
              <div className="flex items-center gap-2">
	                <span className="text-slate-900 font-bold mr-1 text-sm leading-5">
	                  {tier.value.toLocaleString()}
	                </span>
                <CustomerActiveIcon />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
